/**
 * WorkspaceRelayPtyAdapter — web + desktop-renderer PTY adapter backed by
 * the Hub WorkspaceRelay DO (KTK-122/125). All PTY I/O round-trips through
 * the existing relay WSS; neither shell needs local node-pty.
 *
 * Wire protocol (per rocky, 2026-04-19):
 *   Client → Hub → daemon:
 *     session:list
 *     session:attach { sessionId }
 *     session:detach { sessionId }
 *     session:input { sessionId, data: string }
 *     session:resize { sessionId, cols, rows }
 *     session:kill { sessionId }
 *   Daemon → Hub → client:
 *     session:list { sessions: [...] }
 *     session:scrollback { sessionId, data } → xterm should reset + replay
 *     session:output { sessionId, data, seq }
 *     session:exited { sessionId, code?, signal? }
 *     session:error { sessionId?, message }
 */
export function createRelayPtyAdapter(hub, relay) {
    const sinks = new Map();
    // Single relay listener — routes session:* frames to the right sink.
    relay.onMessage((msg) => {
        const type = msg.type;
        if (typeof type !== "string" || !type.startsWith("session:"))
            return;
        const sessionId = msg.sessionId;
        if (!sessionId)
            return;
        const sink = sinks.get(sessionId);
        if (!sink)
            return;
        if (type === "session:scrollback") {
            sink.pushFrame({ kind: "reset" });
            const data = msg.data;
            if (typeof data === "string" && data.length > 0) {
                sink.pushFrame({ kind: "data", bytes: encodeString(data) });
            }
        }
        else if (type === "session:output") {
            const data = msg.data;
            if (typeof data === "string") {
                sink.pushFrame({ kind: "data", bytes: encodeString(data) });
            }
        }
        else if (type === "session:exited") {
            sink.pushFrame({
                kind: "exit",
                code: typeof msg.code === "number" ? msg.code : null,
                signal: typeof msg.signal === "string" ? msg.signal : null,
            });
            sink.close();
        }
        else if (type === "session:error") {
            sink.pushError(new Error(typeof msg.message === "string" ? msg.message : "PTY session error"));
        }
    });
    function createSink() {
        const pending = [];
        const resolvers = [];
        let pendingError = null;
        let closed = false;
        return {
            pushFrame(frame) {
                if (closed)
                    return;
                const resolver = resolvers.shift();
                if (resolver)
                    resolver({ value: frame, done: false });
                else
                    pending.push(frame);
            },
            pushError(err) {
                pendingError = err;
                const resolver = resolvers.shift();
                if (resolver)
                    resolver({ value: undefined, done: true });
            },
            close() {
                closed = true;
                for (const r of resolvers)
                    r({ value: undefined, done: true });
                resolvers.length = 0;
            },
            frames: {
                [Symbol.asyncIterator]() {
                    return {
                        next() {
                            if (pendingError) {
                                const err = pendingError;
                                pendingError = null;
                                return Promise.reject(err);
                            }
                            const next = pending.shift();
                            if (next !== undefined)
                                return Promise.resolve({ value: next, done: false });
                            if (closed)
                                return Promise.resolve({ value: undefined, done: true });
                            return new Promise((resolve) => resolvers.push(resolve));
                        },
                        return() {
                            closed = true;
                            return Promise.resolve({ value: undefined, done: true });
                        },
                    };
                },
            },
        };
    }
    // Host-change pollers
    const hostListeners = new Set();
    let pollTimer = null;
    function ensurePolling() {
        if (pollTimer || hostListeners.size === 0)
            return;
        const tick = async () => {
            try {
                const hosts = await listHostsInner();
                for (const cb of hostListeners)
                    cb(hosts);
            }
            catch {
                /* ignore */
            }
        };
        void tick();
        pollTimer = setInterval(() => void tick(), 30_000);
    }
    function stopPolling() {
        if (hostListeners.size > 0 || !pollTimer)
            return;
        clearInterval(pollTimer);
        pollTimer = null;
    }
    // KTK-374 — both host families, merged. This list fed the terminal and
    // presence loops from pty_hosts alone, which only worked while at least
    // one pty daemon existed (the session:list broadcast that runtime hosts
    // answer was incidentally triggered by iterating pty hosts). Runtime
    // hosts must be first-class here or every session surface goes dark at
    // daemon retirement. The runtime fetch degrades to pty-only on error —
    // loudly (KTK-367): a silently missing family reads as "host not
    // registered" and sends debugging to the wrong layer.
    async function listHostsInner() {
        const [pty, runtime] = await Promise.all([
            hub.listPtyHosts(),
            hub.listAgentRuntimeHosts().catch((e) => {
                console.warn("[relay-pty-adapter] runtime host list failed; host list is pty-only:", e);
                return [];
            }),
        ]);
        const byId = new Map();
        for (const r of pty.filter((h) => !h.revoked_at)) {
            byId.set(r.host_id, {
                id: r.host_id,
                label: r.display_name,
                platform: r.platform,
                online: r.last_seen_at !== null,
            });
        }
        // The legacy dupe rows (KTK-343, cleanup deferred) can put one machine
        // in both tables under the same host_id — the runtime row wins: it's
        // the live family and its last_seen_at is stamped by the relay.
        for (const r of runtime) {
            byId.set(r.host_id, {
                id: r.host_id,
                label: r.display_name,
                platform: narrowPlatform(r.platform),
                online: r.last_seen_at != null,
            });
        }
        return [...byId.values()];
    }
    return {
        async listHosts() {
            return listHostsInner();
        },
        async listSessions(hostId) {
            // Hub fans `session:list` out to every connected daemon and each
            // responds independently, so one send fires N frames. Accumulating by
            // session id across a short window merges them; the old
            // resolve-on-first-frame implementation silently dropped every
            // daemon's response except whichever arrived first — which is why the
            // web sidebar was showing "not connected" for agents running on a
            // daemon other than the one that replied first.
            //
            // `hostId` is retained in the signature for per-host callers but is
            // only used to tag sessions whose frames don't carry their own host
            // id. Neither Shell nor AgentTerminal actually read `hostId` off the
            // session today, so the tag is cosmetic.
            const ACCUMULATION_MS = 800;
            return new Promise((resolve) => {
                const accumulated = new Map();
                // KTK-392 — an empty result is ambiguous: "no host has a session for
                // anyone" and "no host answered inside the window" are the same empty
                // array to every caller. Counting the frames separates them, and it
                // is the datum the next diagnosis will want: three of us spent an
                // evening establishing that replies *were* arriving.
                let frames = 0;
                const off = relay.onMessage((msg) => {
                    if (msg.type !== "session:list")
                        return;
                    frames++;
                    const raw = Array.isArray(msg.sessions) ? msg.sessions : [];
                    const frameHostId = typeof msg.hostId === "string" ? msg.hostId : hostId;
                    for (const s of raw) {
                        const id = typeof s.id === "string" ? s.id : null;
                        if (!id)
                            continue;
                        accumulated.set(id, {
                            id: id,
                            hostId: frameHostId,
                            // Kritaka's convention: sessionId === agentId (confirmed for
                            // today's daemon build). Preserve the mapping so AgentTerminal
                            // can find its session by agent id.
                            agentId: id,
                            cols: typeof s.cols === "number" ? s.cols : 80,
                            rows: typeof s.rows === "number" ? s.rows : 24,
                            createdAt: typeof s.createdAt === "string" ? Date.parse(s.createdAt) : Date.now(),
                            lastOutputAt: typeof s.lastOutputAt === "string" ? s.lastOutputAt : undefined,
                        });
                    }
                });
                setTimeout(() => {
                    off();
                    const sessions = [...accumulated.values()];
                    if (sessions.length === 0) {
                        console.debug(`[relay-pty-adapter] session:list window closed empty after ${ACCUMULATION_MS}ms — ` +
                            `${frames} frame(s) received, relay ${relay.isConnected() ? "connected" : "NOT connected"}. ` +
                            `0 frames means nothing answered in the window; >0 means hosts answered with no sessions.`);
                    }
                    resolve(sessions);
                }, ACCUMULATION_MS);
                relay.send({ type: "session:list" });
            });
        },
        async attach(sessionId, options) {
            const sink = createSink();
            sinks.set(sessionId, sink);
            relay.send({
                type: "session:attach",
                sessionId,
                cols: options.cols,
                rows: options.rows,
                replayScrollback: options.replayScrollback ?? true,
            });
            return {
                sessionId,
                frames: sink.frames,
                async write(input) {
                    const data = typeof input === "string" ? input : decodeBytes(input);
                    relay.send({ type: "session:input", sessionId, data });
                },
                async resize(cols, rows) {
                    relay.send({ type: "session:resize", sessionId, cols, rows });
                },
                async kill() {
                    relay.send({ type: "session:kill", sessionId });
                },
                async detach() {
                    relay.send({ type: "session:detach", sessionId });
                    sinks.delete(sessionId);
                    sink.close();
                },
            };
        },
        onHostsChanged(listener) {
            hostListeners.add(listener);
            ensurePolling();
            return () => {
                hostListeners.delete(listener);
                stopPolling();
            };
        },
    };
}
const encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
const decoder = typeof TextDecoder !== "undefined" ? new TextDecoder() : null;
function encodeString(data) {
    if (encoder)
        return encoder.encode(data);
    const out = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++)
        out[i] = data.charCodeAt(i) & 0xff;
    return out;
}
// AgentRuntimeHost.platform is an open string union; PtyHost's is closed.
// Nothing reads platform off this adapter today, so an unrecognized value
// safely defaults rather than widening the adapter type.
function narrowPlatform(p) {
    return p === "darwin" || p === "win32" ? p : "linux";
}
function decodeBytes(bytes) {
    if (decoder)
        return decoder.decode(bytes);
    let s = "";
    for (const b of bytes)
        s += String.fromCharCode(b);
    return s;
}
//# sourceMappingURL=relay-pty-adapter.js.map